import React, { useState } from 'react';
import { colors, typography, spacing } from '../styles/design-system';

interface BasketballDataDisplayProps {
  data: any;
  homeTeam: string;
  awayTeam: string;
}

const BasketballDataDisplay: React.FC<BasketballDataDisplayProps> = ({ data, homeTeam, awayTeam }) => {
  const [selectedTeam, setSelectedTeam] = useState<'home' | 'away'>('home');

  // 수신 데이터 디버그 로그
  React.useEffect(() => {
    console.log('=== BasketballDataDisplay 디버그 ===');
    console.log('전체 데이터:', data);
    console.log('데이터 키:', Object.keys(data || {}));

    // 시즌 전적
    console.log('시즌_기록:', data?.season_records);
    console.log('시즌_기록.current:', data?.season_records?.current);
    console.log('시즌_기록.current 길이:', data?.season_records?.current?.length || 0);

    // 팀 통계
    console.log('팀_통계:', data?.team_stats);
    console.log('팀_통계.tables:', data?.team_stats?.tables);
    console.log('팀_통계.tables 길이:', data?.team_stats?.tables?.length || 0);
    console.log('팀_통계.3시즌:', data?.team_stats?.three_season);
    console.log('팀_통계.홈_최근:', data?.team_stats?.home_recent);
    console.log('팀_통계.원정_최근:', data?.team_stats?.away_recent);

    // 시즌 선수 순위
    console.log('시즌_선수_랭킹:', data?.season_player_rankings);
    console.log('시즌_선수_랭킹.홈팀:', data?.season_player_rankings?.home_team);
    console.log('시즌_선수_랭킹.원정팀:', data?.season_player_rankings?.away_team);

    // 상대전적
    console.log('상대전적:', data?.head_to_head);
    const h2hRecords = data?.head_to_head?.records || [];
    console.log('상대전적.records 길이:', h2hRecords.length);

    // 최근 전적
    console.log('최근_기록:', data?.recent_records);
    console.log('최근_기록 길이:', data?.recent_records?.length || 0);

    // 선수 통계
    console.log('선수_통계:', data?.player_stats);
    console.log('선수_통계.data:', data?.player_stats?.data);
    console.log('선수_통계.data 길이:', data?.player_stats?.data?.length || 0);

    console.log('===================================');
  }, [data]);

  // 데이터 구조에서 추출
  const headToHead = data.head_to_head?.records || [];
  const teamStats = data.team_stats || {};
  const seasonRecords = data.season_records || {};
  const playerStats = data.player_stats || {};
  const recentRecords = data.recent_records || [];
  const seasonPlayerRankings = data.season_player_rankings || {};
  const venue = data.venue;
  const dateTime = data.date_time;

  // 헤더와 데이터를 적절히 처리하여 테이블 렌더링
  const renderTable = (tableData: any, title?: string): React.ReactElement | null => {
    if (!tableData) return null;

    let headers: string[] = [];
    let dataArray: any[][] = [];
    let description = '';

    // 다양한 데이터 형식 처리
    if (tableData.headers && tableData.data) {
      // 새 형식: { headers: [...], data: [[...], [...]], description: '...' }
      headers = tableData.headers;
      dataArray = tableData.data;
      description = tableData.description || '';
    } else if (tableData.data && Array.isArray(tableData.data)) {
      // 기존 형식: { data: [[headers], [...], [...]], description: '...' }
      const rawData = tableData.data;
      description = tableData.description || '';

      // 헤더와 데이터 추출
      if (rawData.length > 0) {
        // 헤더 행 감지 시도
        let headerRowIndex = 0;

        // 첫 번째 행이 제목인지 확인 (단일 셀)
        if (Array.isArray(rawData[0]) && rawData[0].length === 1) {
          if (!title && !description) {
            description = rawData[0][0];
          }
          headerRowIndex = 1;
        }

        if (rawData.length > headerRowIndex) {
          headers = rawData[headerRowIndex];
          dataArray = rawData.slice(headerRowIndex + 1);
        }
      }
    } else if (Array.isArray(tableData)) {
      // 직접 배열 형식: [[headers], [...], [...]]
      if (tableData.length > 0) {
        // 제목 행이 있으면 건너뜀
        let startIdx = 0;
        if (tableData[0] && tableData[0].length === 1) {
          if (!title) {
            title = tableData[0][0];
          }
          startIdx = 1;
        }

        if (tableData.length > startIdx) {
          headers = tableData[startIdx];
          dataArray = tableData.slice(startIdx + 1);
        }
      }
    } else {
      return null;
    }

    // 데이터 유효성 검사
    if (dataArray.length === 0) return null;

    // 헤더가 비어있지만 데이터가 있으면 컬럼 헤더 생성
    // (백엔드에서 이미 처리하지만 추가 안전장치)
    if (!headers || headers.length === 0) {
      if (dataArray.length > 0 && Array.isArray(dataArray[0])) {
        // 백엔드 형식에 맞춰 일반 컬럼 헤더 생성
        headers = dataArray[0].map((_, idx) => `열${idx + 1}`);
        console.warn('프론트엔드에서 헤더가 비어있어 컬럼명 생성:', headers);
      } else {
        return null;
      }
    }

    const tableTitle = title || description;

    return (
      <div style={styles.tableContainer}>
        {tableTitle && <h4 style={styles.tableTitle}>{tableTitle}</h4>}
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {headers.map((header: string, idx: number) => (
                  <th key={idx} style={{
                    ...styles.th,
                    backgroundColor: idx === 0 ? '#f1f3f5' : '#f8f9fa'
                  }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataArray.map((row: any[], rowIdx: number) => {
                // 유효하지 않은 행 건너뜀
                if (!Array.isArray(row)) return null;

                return (
                  <tr key={rowIdx} style={{
                    backgroundColor: rowIdx % 2 === 0 ? '#ffffff' : '#fafbfc'
                  }}>
                    {headers.map((header: string, cellIdx: number) => (
                      <td key={cellIdx} style={{
                        ...styles.td,
                        fontWeight: cellIdx === 0 ? 600 : 400
                      }}>{row[cellIdx] || '-'}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // 팀별 데이터 추출
  const getTeamData = () => {
    const isHome = selectedTeam === 'home';

    return {
      seasonRecords: seasonRecords.current?.filter((record: any) => record.team_type === selectedTeam) || [],
      threeSeasonStats: teamStats.three_season?.filter((stat: any) => stat.team_type === selectedTeam) || [],
      recentGames: isHome ? teamStats.home_recent : teamStats.away_recent,
      playerRankings: isHome ? seasonPlayerRankings.home_team : seasonPlayerRankings.away_team,
    };
  };

  const teamData = getTeamData();

  // 팀 데이터 디버그
  React.useEffect(() => {
    console.log('=== 팀 데이터 디버그 ===');
    console.log('선택된 팀:', selectedTeam);
    console.log('팀 데이터:', teamData);
    console.log('시즌 기록:', teamData.seasonRecords);
    console.log('3시즌 통계:', teamData.threeSeasonStats);
    console.log('최근 경기:', teamData.recentGames);
    console.log('선수 랭킹:', teamData.playerRankings);
    console.log('======================');
  }, [selectedTeam, teamData]);

  // 표시할 데이터가 있는지 확인
  const hasData = (
    (seasonRecords && seasonRecords.current && seasonRecords.current.length > 0) ||
    (headToHead && headToHead.length > 0) ||
    (teamStats && (teamStats.tables || teamStats.three_season || teamStats.home_recent || teamStats.away_recent)) ||
    (playerStats && playerStats.data && playerStats.data.length > 0) ||
    (seasonPlayerRankings && (seasonPlayerRankings.home_team || seasonPlayerRankings.away_team))
  );

  if (!hasData) {
    return (
      <div style={styles.emptyState}>
        <div style={styles.emptyIcon}></div>
        <h3 style={styles.emptyTitle}>데이터가 없습니다</h3>
        <p style={styles.emptyMessage}>
          현재 이 경기에 대한 통계 데이터가 수집되지 않았음.<br />
          잠시 후 다시 시도해.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Match Info Header */}
      {(dateTime || venue) && (
        <div style={styles.matchInfoHeader}>
          <h3 style={styles.matchTitle}>
            {homeTeam} vs {awayTeam}
          </h3>
          <div style={styles.matchDetails}>
            {dateTime && <span>{dateTime}</span>}
            {venue && <span> {dateTime ? '| ' : ''}{venue}</span>}
          </div>
        </div>
      )}

      {/* Team Selection Buttons */}
      <div style={styles.teamButtons}>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'home' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('home')}
        >
          홈팀: {homeTeam}
        </button>
        <button
          style={{
            ...styles.teamButton,
            ...(selectedTeam === 'away' ? styles.selectedTeamButton : {})
          }}
          onClick={() => setSelectedTeam('away')}
        >
          원정팀: {awayTeam}
        </button>
      </div>

      {/* Team Content */}
      <div style={styles.content}>
        <h3 style={styles.teamNameTitle}>
          {selectedTeam === 'home' ? homeTeam : awayTeam} 상세 데이터
        </h3>

        {/* Debug info for empty sections */}
        {teamData.seasonRecords.length === 0 &&
         teamData.threeSeasonStats.length === 0 &&
         !teamData.recentGames &&
         (!teamData.playerRankings || teamData.playerRankings.length === 0) && (
          <div style={{padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px', marginBottom: '20px'}}>
            <p style={{margin: 0, color: '#856404'}}>
              {selectedTeam === 'home' ? homeTeam : awayTeam} 팀의 상세 데이터가 수집되지 않았음.
            </p>
          </div>
        )}

        {/* Season Records Section */}
        {teamData.seasonRecords.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>시즌 전적</h3>
            {teamData.seasonRecords.map((record: any, idx: number) => (
              <div key={idx}>
                {renderTable(record)}
              </div>
            ))}
          </div>
        )}

        {/* Team Stats Section */}
        {(teamData.recentGames || teamData.threeSeasonStats.length > 0) && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>팀 통계</h3>
            {teamData.recentGames && (
              <>
                <h4 style={styles.subsectionTitle}>최근 경기</h4>
                {renderTable(teamData.recentGames)}
              </>
            )}
            {teamData.threeSeasonStats.length > 0 && (
              <>
                <h4 style={styles.subsectionTitle}>3시즌 통계</h4>
                {teamData.threeSeasonStats.map((stat: any, idx: number) => (
                  <div key={idx}>
                    {renderTable(stat)}
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Season Player Rankings Section */}
        {teamData.playerRankings && teamData.playerRankings.length > 0 && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>시즌 선수 순위</h3>
            {teamData.playerRankings.map((ranking: any, idx: number) => (
              <div key={idx}>
                {renderTable(ranking)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Head to Head Section (Both Teams) */}
      {headToHead && headToHead.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>상대 전적</h3>
          {headToHead.map((h2h: any, idx: number) => (
            <div key={idx}>
              {renderTable(h2h)}
            </div>
          ))}
        </div>
      )}

      {/* General Team Stats (Both Teams) */}
      {teamStats && teamStats.tables && teamStats.tables.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>팀 비교 통계</h3>
          {teamStats.tables.map((table: any, idx: number) => (
            <div key={idx}>
              {renderTable(table)}
            </div>
          ))}
        </div>
      )}

      {/* Recent Records (Both Teams) */}
      {recentRecords && recentRecords.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>최근 전적</h3>
          {recentRecords.map((record: any, idx: number) => (
            <div key={idx}>
              {renderTable(record)}
            </div>
          ))}
        </div>
      )}

      {/* Player Stats (Both Teams) */}
      {playerStats && playerStats.data && playerStats.data.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>👤 선수 통계</h3>
          {playerStats.data.map((stat: any, idx: number) => (
            <div key={idx}>
              {renderTable(stat)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxHeight: '60vh',
    padding: spacing[6],
    backgroundColor: colors.white,
    overflow: 'auto' as const,
  },
  matchInfoHeader: {
    marginBottom: spacing[7],
    paddingBottom: spacing[5],
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  matchTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.semibold,
    color: colors.black,
    marginBottom: spacing[3],
  },
  matchDetails: {
    fontSize: typography.fontSize.base,
    color: colors.gray[600],
  },
  teamButtons: {
    display: 'flex',
    marginBottom: spacing[7],
  },
  teamButton: {
    flex: 1,
    padding: spacing[4],
    backgroundColor: colors.white,
    color: colors.gray[600],
    border: `1px solid ${colors.gray[300]}`,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  selectedTeamButton: {
    backgroundColor: colors.black,
    color: colors.white,
    border: `1px solid ${colors.black}`,
  },
  content: {
    marginBottom: spacing[6],
  },
  teamNameTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[7],
    color: colors.black,
    paddingLeft: spacing[3],
    borderLeft: `3px solid ${colors.black}`,
  },
  section: {
    marginBottom: spacing[9],
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    marginBottom: spacing[6],
    color: colors.gray[800],
    borderBottom: `1px solid ${colors.gray[300]}`,
    paddingBottom: spacing[3],
  },
  subsectionTitle: {
    margin: `${spacing[6]} 0 ${spacing[4]} 0`,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    color: colors.gray[700],
  },
  tableContainer: {
    marginBottom: spacing[6],
  },
  tableTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.medium,
    marginBottom: spacing[3],
    color: colors.gray[600],
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    border: `1px solid ${colors.gray[200]}`,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: typography.fontSize.base,
  },
  th: {
    padding: `${spacing[4]} ${spacing[3]}`,
    backgroundColor: colors.gray[100],
    borderBottom: `1px solid ${colors.gray[300]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    fontWeight: typography.fontWeight.semibold,
    textAlign: 'left' as const,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[800],
  } as React.CSSProperties,
  td: {
    padding: spacing[3],
    borderBottom: `1px solid ${colors.gray[200]}`,
    borderRight: `1px solid ${colors.gray[200]}`,
    whiteSpace: 'nowrap' as const,
    color: colors.gray[700],
  } as React.CSSProperties,
  emptyState: {
    textAlign: 'center' as const,
    padding: spacing[10],
    backgroundColor: colors.gray[50],
    border: `1px solid ${colors.gray[200]}`,
  } as React.CSSProperties,
  emptyIcon: {
    fontSize: '64px',
    marginBottom: spacing[6],
    opacity: 0.5,
  } as React.CSSProperties,
  emptyTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.semibold,
    color: colors.gray[700],
    marginBottom: spacing[4],
  } as React.CSSProperties,
  emptyMessage: {
    fontSize: typography.fontSize.base,
    color: colors.gray[500],
    lineHeight: typography.lineHeight.relaxed,
  } as React.CSSProperties,
};

export default BasketballDataDisplay;
